/**
 * The single lodging hero — full-bleed photo (or brand-gradient fallback)
 * with an editorial serif heading and the availability search card
 * overlapping the hero's bottom edge.
 *
 * Slot-based so both call sites keep their own wiring:
 *   - `availability_search` CMS section (LodgingSections) passes plain
 *     title/subtitle strings and a bare <img> from `content.hero_image`.
 *   - HeroSection's `search_forward` Look variant passes QuickEdit-wrapped
 *     title/subtitle and its QuickEditImage media node.
 */
import { cn } from '@/lib/utils';

interface LodgingHeroProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  eyebrow?: React.ReactNode;
  /** Small meta row rendered under the heading, above the search card — used
   * for the single-property rating line (★ · reviews · location). Inherits the
   * hero's white-on-photo treatment; keep the contents inline. */
  meta?: React.ReactNode;
  /** Full-bleed background media node; must fill its container (h-full
   * w-full object-cover). Omit for the brand gradient fallback. */
  media?: React.ReactNode;
  /** Rendered in a card pulled up over the hero's bottom edge. */
  searchBar?: React.ReactNode;
  headingId?: string;
  /** data-hero-variant attribute (Look-system hook, e.g. "search_forward"). */
  variantAttr?: string;
  className?: string;
}

const HEADING_SHADOW = { textShadow: '0 2px 12px rgba(0,0,0,0.3)' };

export default function LodgingHero({
  title,
  subtitle,
  eyebrow,
  meta,
  media,
  searchBar,
  headingId = 'lodging-hero-heading',
  variantAttr,
  className,
}: LodgingHeroProps) {
  return (
    <section aria-labelledby={headingId} data-hero-variant={variantAttr} className={cn('relative', className)}>
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          {media ?? (
            <div
              data-testid="lodging-hero-fallback"
              className="h-full w-full bg-gradient-to-br from-sage-700 via-sage-800 to-sage-900"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" aria-hidden="true" />
        </div>
        <div
          className={cn(
            'relative mx-auto max-w-6xl px-4 text-center sm:px-6',
            // Extra bottom padding leaves room for the overlapping search card.
            searchBar ? 'pt-20 pb-24 sm:pt-24 sm:pb-28 lg:pt-32 lg:pb-32' : 'py-20 sm:py-24 lg:py-32',
          )}
        >
          {eyebrow && (
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/85" style={HEADING_SHADOW}>
              {eyebrow}
            </p>
          )}
          <h1
            id={headingId}
            className="mx-auto max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl"
            style={HEADING_SHADOW}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mx-auto mt-4 max-w-2xl font-display text-lg italic text-white/90 sm:mt-5 sm:text-xl">
              {subtitle}
            </p>
          )}
          {meta && (
            <div
              className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-white/90 sm:mt-5"
              style={HEADING_SHADOW}
            >
              {meta}
            </div>
          )}
        </div>
      </div>
      {searchBar && (
        <div className="relative z-10 mx-auto -mt-10 max-w-6xl px-4 sm:-mt-12 sm:px-6">
          {searchBar}
        </div>
      )}
    </section>
  );
}
